/**
 * Wasabi S3 Configuration
 * 
 * Note: Wasabi credentials are loaded from environment variables.
 * Set the following in your .env file:
 * - REACT_APP_WASABI_ACCESS_KEY
 * - REACT_APP_WASABI_SECRET_KEY
 * - REACT_APP_WASABI_REGION (optional, defaults to eu-west-1)
 * - REACT_APP_WASABI_BUCKET (optional, defaults to digitalassetsms-staging)
 * - REACT_APP_WASABI_ENDPOINT (optional, auto-detected from region)
 * 
 * Wasabi is S3-compatible, so the backend can use AWS SDK to interact with it.
 */

// Wasabi Configuration
// Note: 301 error fix - Use correct endpoint based on your bucket region

// Common Wasabi Endpoints
const WASABI_ENDPOINTS: Record<string, string> = {
  'us-east-1': 'https://s3.us-east-1.wasabisys.com',
  'us-east-2': 'https://s3.us-east-2.wasabisys.com', 
  'us-west-1': 'https://s3.us-west-1.wasabisys.com',
  'eu-central-1': 'https://s3.eu-central-1.wasabisys.com',
  'eu-west-1': 'https://s3.eu-west-1.wasabisys.com',
  'ap-northeast-1': 'https://s3.ap-northeast-1.wasabisys.com',
  'ap-southeast-1': 'https://s3.ap-southeast-1.wasabisys.com',
  // Default endpoint (some regions use this)
  'default': 'https://s3.wasabisys.com',
};

// Get endpoint - try region-specific first, fallback to default
const getWasabiEndpoint = (region?: string): string => {
  if (process.env.REACT_APP_WASABI_ENDPOINT) {
    return process.env.REACT_APP_WASABI_ENDPOINT;
  }
  
  if (region && WASABI_ENDPOINTS[region]) {
    return WASABI_ENDPOINTS[region];
  }
  
  // Default endpoint - works for most Wasabi buckets
  return WASABI_ENDPOINTS.default;
};

// Get region from env or use eu-west-1 (your region)
// Hardcoded to eu-west-1 to fix 301 error
const WASABI_REGION = process.env.REACT_APP_WASABI_REGION || 'eu-west-1';
const WASABI_ENDPOINT = getWasabiEndpoint(WASABI_REGION);

// Debug: Log configuration (remove in production)
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Wasabi Config:', {
    REGION: WASABI_REGION,
    ENDPOINT: WASABI_ENDPOINT,
    BUCKET: process.env.REACT_APP_WASABI_BUCKET || 'digitalassetsms-staging',
  });
}

// Validate required environment variables
const WASABI_ACCESS_KEY = process.env.REACT_APP_WASABI_ACCESS_KEY;
const WASABI_SECRET_KEY = process.env.REACT_APP_WASABI_SECRET_KEY;

if (!WASABI_ACCESS_KEY || !WASABI_SECRET_KEY) {
  console.error('❌ Wasabi credentials are missing! Please set REACT_APP_WASABI_ACCESS_KEY and REACT_APP_WASABI_SECRET_KEY in your .env file');
}

export const WASABI_CONFIG = {
  ACCESS_KEY: WASABI_ACCESS_KEY || '',
  SECRET_KEY: WASABI_SECRET_KEY || '',
  // Region
  REGION: WASABI_REGION,
  // Endpoint
  ENDPOINT: WASABI_ENDPOINT,
  // Bucket name
  BUCKET: process.env.REACT_APP_WASABI_BUCKET || 'digitalassetsms-staging',
};

/**
 * Backend API endpoints for Wasabi operations:
 * 
 * POST /api/assets/init-upload - Initialize multipart upload
 * POST /api/assets/upload-chunk - Upload a chunk
 * POST /api/assets/complete-upload - Complete multipart upload
 * POST /api/assets/upload - Direct upload (for smaller files)
 * GET /api/assets/{id}/download-url - Get presigned download URL
 */

